import './CustomNativeDocsAccordion.scss';
import React from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {ReactComponent as DeleteIcon} from '../../../../../assets/images/delete.svg';
import ArrButton from '../../../../common/arr-button/ArrButton';
import TagsSection from '../tags-section/TagsSection';
import TextPropertyToolbar from '../text-property-toolbar/TextPropertyToolbar';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../../../shared/interfaces';
import {TagItem, FileTypeItem} from '../../../../../api/types/production.type';
import {ISelectItem} from '../../../../../shared/types';
import {t} from 'i18next';
import CustomFileTypeSelector from '../custom-file-type-selector/CustomFileTypeSelector';

export interface NativelyDocs {
  placeholderText: string;
  selectedTags: TagItem[];
  disabledTags?: TagItem[];
  brandingFont: string;
  brandingFontStyle: string;
  brandingFontSize: number;
  fileTypes: FileTypeItem[];
}

interface CustomNativeDocsAccordionProps {
  id: string;
  index: number;
  nativeDocsData: NativelyDocs;
  onDelete: (id: string) => void;
  onNativeDocsDataChange: (id: string, data: NativelyDocs) => void;
  tagsTreeData?: TreeViewBaseItem<ExtendedTreeItemProps>[];
  insertFieldTypes: ISelectItem[];
  metadataFieldOptions: ISelectItem[];
  productionFieldOptions: ISelectItem[];
  placeholder?: string;
  disabled?: boolean;
}

const CustomNativeDocsAccordion: React.FC<CustomNativeDocsAccordionProps> = ({
  id,
  index,
  nativeDocsData,
  onDelete,
  onNativeDocsDataChange,
  tagsTreeData = [],
  insertFieldTypes,
  metadataFieldOptions,
  productionFieldOptions,
  placeholder,
  disabled = false,
}) => {
  const handleTagsChange = (tags: TagItem[]) => {
    if (disabled) return;
    onNativeDocsDataChange(id, {
      ...nativeDocsData,
      selectedTags: tags,
    });
  };

  const handleTextChange = (text: string) => {
    if (disabled) return;
    onNativeDocsDataChange(id, {
      ...nativeDocsData,
      placeholderText: text,
    });
  };
  const handleFileTypesChange = (fileTypesUpdated: FileTypeItem[]) => {
    if (disabled) return;
    onNativeDocsDataChange(id, {
      ...nativeDocsData,
      fileTypes: fileTypesUpdated,
    });
  };
  return (
    <Box className="custom-native-docs-accordion-wrapper">
      <Box className="custom-native-docs-header-wrapper">
        <Accordion className="custom-native-docs-accordion" defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className="custom-native-docs-accordion-summary"
          >
            <Typography
              className="typo-text subhead4_size custom-native-docs-title"
              data-testid={`custom-native-docs-accordion-title`}
            >
              {t(
                'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.placeholders.nativelyProducedDocs.title',
                {number: index + 1}
              )}
            </Typography>
          </AccordionSummary>

          <AccordionDetails className="custom-native-docs-details">
            <Box className="custom-native-docs-content">
              {/* File Types Selector */}
              <CustomFileTypeSelector
                onFileTypesChange={handleFileTypesChange}
                fileTypes={nativeDocsData.fileTypes}
                disabled={disabled}
                checkboxDataTestId={`custom-native-docs-file-type-selector-${id}`}
              />
              {/* Tags Section */}
              <TagsSection
                selectedTags={nativeDocsData.selectedTags}
                onTagsChange={handleTagsChange}
                tagsTreeData={tagsTreeData}
                disabledTags={nativeDocsData.disabledTags}
                disabled={disabled}
              />

              {/* Text Property Toolbar */}
              <TextPropertyToolbar
                variant="with-title"
                title="Placeholder Text"
                dataTestId="native-docs-data"
                toolbarText={nativeDocsData.placeholderText}
                insertFieldTypes={insertFieldTypes}
                metadataFieldOptions={metadataFieldOptions}
                productionFieldOptions={productionFieldOptions}
                onToolbarTextChange={handleTextChange}
                onInsertField={(field) => {
                  handleTextChange(
                    `${nativeDocsData.placeholderText}$${field}$`
                  );
                }}
                placeholder={placeholder}
                disabled={disabled}
                textAreaTestId={`native-docs-placeholder-${index}`}
              />
            </Box>
          </AccordionDetails>
        </Accordion>
        {!disabled && (
          <ArrButton
            variant="secondary"
            size="small"
            onClick={() => onDelete(id)}
            className="delete-btn"
            dataTestId="custom-native-docs-accordion-button"
          >
            <DeleteIcon />
          </ArrButton>
        )}
      </Box>
    </Box>
  );
};

export default CustomNativeDocsAccordion;
